import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Smartphone, Book, Bath, Moon, CheckCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function ScreenFreeEveningGuide() {
  const navigate = useNavigate();

  const alternatives = [
    { activity: "Read a Physical Book", icon: Book, tip: "Fiction is especially relaxing—avoid work-related reading" },
    { activity: "Take a Warm Bath", icon: Bath, tip: "The temperature drop after helps trigger sleepiness" },
    { activity: "Gentle Stretching", icon: Moon, tip: "5-10 minutes of easy yoga or stretches" },
    { activity: "Journal", icon: Book, tip: "Write thoughts to clear your mind before bed" },
    { activity: "Talk with Family", icon: Book, tip: "Real conversation, no phones at the table" },
    { activity: "Listen to Music or Podcast", icon: Moon, tip: "Calming content, not news or drama" },
    { activity: "Puzzles or Cards", icon: Book, tip: "Low-key mental activity that's not stimulating" },
    { activity: "Skincare Routine", icon: Moon, tip: "Make it a relaxing ritual, not a rushed task" }
  ];

  const blueLightFacts = [
    { stat: "50%", fact: "Blue light suppresses melatonin production by up to 50%" },
    { stat: "30 min", fact: "Screen use before bed can delay sleep onset by 30+ minutes" },
    { stat: "2 hours", fact: "Even 2 hours of screen exposure shifts circadian rhythm" },
    { stat: "Less REM", fact: "Screen users get less restorative REM sleep" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-4">
            Week 5 • Day 3 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Screen-Free Evening
          </h1>
          <p className="text-xl text-secondary-600">Disconnect to Rest Better</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-purple-600 to-violet-600 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Smartphone className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Tonight's Challenge</h3>
              <p className="text-purple-100 text-lg">
                Set an alarm for 60 minutes before bedtime. When it rings, put ALL devices in another room. 
                No exceptions. This one change can dramatically improve your sleep quality.
              </p>
            </div>
          </div>
        </section>

        {/* The Science */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">The Science of Blue Light</h2>
          <p className="text-secondary-700 mb-6">
            Screens emit blue light that tricks your brain into thinking it's daytime. This suppresses melatonin, 
            the hormone that makes you sleepy. Even with night mode on, the stimulation of content keeps your brain alert.
          </p>
          <div className="grid md:grid-cols-4 gap-4">
            {blueLightFacts.map((item, i) => (
              <div key={i} className="text-center p-4 bg-purple-50 rounded-xl">
                <p className="text-2xl font-bold text-purple-600">{item.stat}</p>
                <p className="text-sm text-secondary-600">{item.fact}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Alternatives */}
        <section className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-2xl p-8 border-2 border-purple-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">What to Do Instead</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {alternatives.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4 flex items-start gap-3">
                <item.icon className="w-6 h-6 text-purple-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-secondary-900">{item.activity}</h3>
                  <p className="text-sm text-secondary-600">{item.tip}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Practical Tips */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Clock className="w-6 h-6 text-purple-500" />
            Making It Easier
          </h2>
          <div className="space-y-3">
            {[
              "Charge your phone in another room overnight",
              "Get an old-fashioned alarm clock",
              "Use app timers to lock yourself out of social media after 9 PM",
              "Tell family members about your new rule so they don't text late",
              "Prepare tomorrow's to-do list before putting devices away",
              "If you must use a device, use reading mode + lowest brightness"
            ].map((tip, i) => (
              <div key={i} className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg text-secondary-700">
                <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0" />
                {tip}
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default ScreenFreeEveningGuide;
